import { Box, Typography, Chip, Skeleton } from '@mui/material'
import { AccessTime as ReadTimeIcon } from '@mui/icons-material'
import DOMPurify from 'dompurify'

export interface ArticleViewerProps {
  title: string
  content: string // HTML content (converted from markdown)
  category?: string
  tags?: string[]
  coverImageUrl?: string
  estimatedReadTime?: number
  isLoading?: boolean
  /** Compact mode for sidebar inline display (smaller fonts, less padding) */
  compact?: boolean
}

/**
 * Shared read-only article viewer component.
 * Theme-aware - works in both light mode (core) and dark mode (admin).
 * Renders HTML article content with proper typography styling.
 */
export function ArticleViewer({
  title,
  content,
  category,
  tags: _tags,
  coverImageUrl,
  estimatedReadTime,
  isLoading,
  compact,
}: ArticleViewerProps) {
  if (isLoading) {
    return <ArticleViewerSkeleton compact={compact} />
  }

  let strippedContent = content.replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, '')
  if (title) {
    strippedContent = strippedContent.replace(
      new RegExp(
        `^\\s*<h1[^>]*>\\s*${title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*<\\/h1>`,
        'i'
      ),
      ''
    )
  }
  const cleanedContent = DOMPurify.sanitize(strippedContent, {
    ADD_TAGS: ['iframe'],
    ADD_ATTR: [
      'allow',
      'allowfullscreen',
      'frameborder',
      'scrolling',
      'data-video-src',
      'data-video-platform',
      'referrerpolicy',
      'style',
    ],
  })

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'auto',
      }}
    >
      {/* Cover Image */}
      {coverImageUrl && (
        <Box
          sx={{
            width: '100%',
            maxHeight: compact ? 160 : 300,
            overflow: 'hidden',
            borderRadius: compact ? 0 : 1,
            mb: compact ? 2 : 3,
          }}
        >
          <Box
            component="img"
            src={coverImageUrl}
            alt={title}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </Box>
      )}

      {/* Metadata row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: compact ? 1 : 2,
          px: compact ? 0 : 0,
          flexWrap: 'wrap',
        }}
      >
        {category && (
          <Chip
            label={category}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontSize: compact ? '0.625rem' : '0.75rem', height: compact ? 20 : 24 }}
          />
        )}
        {estimatedReadTime && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ReadTimeIcon sx={{ fontSize: compact ? 14 : 16, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {estimatedReadTime} min read
            </Typography>
          </Box>
        )}
      </Box>

      {/* Title - hidden in compact mode since the markdown content includes the heading */}
      {!compact && (
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            mb: 2,
            color: 'text.primary',
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
      )}

      {/* Article Content */}
      <Box
        sx={(theme) => ({
          flex: 1,
          '& > *:first-of-type': {
            marginTop: 0,
          },
          '& h1': {
            ...theme.typography.h5,
            fontWeight: 700,
            mt: compact ? 2 : 3,
            mb: compact ? 0.5 : 1,
            color: 'text.primary',
          },
          '& h2': {
            ...theme.typography.h6,
            fontWeight: 600,
            mt: compact ? 1.5 : 2.5,
            mb: compact ? 0.5 : 1,
            color: 'text.primary',
          },
          '& h3': {
            ...(compact ? theme.typography.subtitle1 : theme.typography.h6),
            fontWeight: 600,
            mt: compact ? 1.5 : 2,
            mb: compact ? 0.5 : 1,
            color: 'text.primary',
          },
          '& h4, & h5, & h6': {
            ...theme.typography.subtitle1,
            fontWeight: 600,
            mt: compact ? 1 : 2,
            mb: compact ? 0.5 : 1,
            color: 'text.primary',
          },
          '& p': {
            ...theme.typography.body2,
            mb: compact ? 0.75 : 1.5,
            lineHeight: compact ? 1.6 : 1.7,
            color: 'text.primary',
            fontSize: compact ? '0.8125rem' : '0.875rem',
          },
          '& p:empty': {
            display: 'none',
          },
          '& ul, & ol': {
            pl: 3,
            mb: compact ? 0.75 : 1.5,
          },
          '& li': {
            ...theme.typography.body2,
            mb: compact ? 0.25 : 0.5,
            lineHeight: compact ? 1.6 : 1.7,
            color: 'text.primary',
            fontSize: compact ? '0.8125rem' : '0.875rem',
          },
          '& a': {
            color: 'primary.main',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          },
          '& code': {
            fontFamily: 'monospace',
            fontSize: '0.85em',
            px: 0.5,
            py: 0.25,
            borderRadius: 0.5,
            bgcolor: 'action.hover',
            color: 'text.primary',
          },
          '& pre': {
            bgcolor: 'action.hover',
            borderRadius: 1,
            p: 2,
            mb: 2,
            overflow: 'auto',
            '& code': {
              bgcolor: 'transparent',
              p: 0,
              fontSize: '0.8125rem',
            },
          },
          '& blockquote': {
            borderLeft: '3px solid',
            borderColor: 'primary.main',
            pl: 2,
            py: 0.5,
            my: 2,
            bgcolor: 'action.hover',
            borderRadius: '0 4px 4px 0',
            '& p': {
              mb: 0,
              fontStyle: 'italic',
            },
          },
          '& table': {
            width: '100%',
            borderCollapse: 'collapse',
            mb: 2,
            '& th, & td': {
              border: '1px solid',
              borderColor: 'divider',
              p: 1,
              textAlign: 'left',
              fontSize: compact ? '0.75rem' : '0.8125rem',
            },
            '& th': {
              bgcolor: 'action.hover',
              fontWeight: 600,
            },
          },
          '& img': {
            maxWidth: '100%',
            height: 'auto',
            borderRadius: 1,
            my: 1,
          },
          '& hr': {
            border: 'none',
            borderTop: '1px solid',
            borderColor: 'divider',
            my: 3,
          },
        })}
        dangerouslySetInnerHTML={{ __html: cleanedContent }}
      />
    </Box>
  )
}

function ArticleViewerSkeleton({ compact }: { compact?: boolean }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <Skeleton variant="rounded" width={80} height={compact ? 20 : 24} />
        <Skeleton variant="rounded" width={60} height={compact ? 20 : 24} />
      </Box>
      <Skeleton variant="text" width="70%" height={compact ? 32 : 48} />
      <Skeleton variant="text" width="100%" height={20} />
      <Skeleton variant="text" width="95%" height={20} />
      <Skeleton variant="text" width="88%" height={20} />
      <Skeleton variant="text" width="100%" height={20} />
      <Skeleton variant="text" width="75%" height={20} />
      <Skeleton variant="text" width="92%" height={20} />
    </Box>
  )
}

export default ArticleViewer
